import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NotificationBell } from "@/modules/scheduling/shared";
import { api } from "@/lib/api";
import { LayoutDashboard, Building2, Layers, ShieldCheck, ScrollText, Inbox, Landmark, UserPlus, LifeBuoy, CircleUser } from "lucide-react";
import { useUnreadPoll } from "@/modules/support/SupportChat";
import { useFetch } from "@/lib/useFetch";
import { AppShell } from "@/components/AppShell";
import { Badge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";

export default function AdminShell() {
  const { me } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  const pending = useFetch("/admin/signup-requests/count");
  const reloadPending = pending.reload;
  const navigate = useNavigate();
  useEffect(() => {
    window.addEventListener("gf:signup-requests", reloadPending);
    const id = setInterval(reloadPending, 20000);
    return () => { window.removeEventListener("gf:signup-requests", reloadPending); clearInterval(id); };
  }, [reloadPending]);
  const supportUnread = useUnreadPoll("/admin/support/unread", (d) => d.messages, true, (d) => d.latest &&
    toast(t("b9.support.toast", { name: d.latest.garage_name }), { description: d.latest.preview, duration: 12000, action: { label: t("b9.support.open"), onClick: () => navigate(`/admin/support-inbox?g=${d.latest.garage_id}`) } }));
  const latest = pending.data?.latest;
  useEffect(() => {
    if (!pending.data) return;
    const seen = sessionStorage.getItem("gf_last_signup_seen");
    const id = latest?.id || "none";
    if (seen && latest && seen !== id) {
      toast(t("p7.approval.toast", { name: latest.garage_name }), { duration: 15000, action: { label: t("p7.approval.review"), onClick: () => navigate("/admin/signup-requests") } });
    }
    sessionStorage.setItem("gf_last_signup_seen", id);
  }, [pending.data, latest, navigate]);
  const I = (to, key, icon, testId, extra = {}) => ({ to, label: t(key), icon, testId, ...extra });
  const groups = [
    { key: "a-home", items: [I("/admin", "b13.nav.home", LayoutDashboard, "admin-nav-dashboard", { end: true })] },
    { key: "a-garages", label: t("admin.nav.garages"), icon: Building2, items: [I("/admin/garages", "admin.nav.garages", Building2, "admin-nav-garages"),
      I("/admin/signup-requests", "p7.approval.nav", UserPlus, "admin-nav-signup-requests", { badge: pending.data?.pending })] },
    { key: "a-plans", items: [I("/admin/plans", "b13.nav.plans", Layers, "admin-nav-plans")] },
    { key: "a-support", label: t("b13.nav.support"), icon: LifeBuoy, items: [I("/admin/support-inbox", "b9.support.inbox", LifeBuoy, "admin-nav-support-inbox", { badge: supportUnread || null }),
      I("/admin/support", "admin.nav.support", ShieldCheck, "admin-nav-support")] },
    { key: "a-audit", label: t("admin.nav.audit"), icon: ScrollText, items: [I("/admin/audit", "admin.nav.audit", ScrollText, "admin-nav-audit"), I("/admin/outbox", "admin.nav.outbox", Inbox, "admin-nav-outbox")] },
  ];
  const bottom = [I("/admin/platform", "b13.nav.platform", Landmark, "admin-nav-platform"), I("/account/security", "b8.account.title", CircleUser, "my-account-link")];
  const bell = <NotificationBell testId="admin-notification-bell" load={() => api.get("/admin/notifications").then((r) => r.data)}
    markRead={() => api.post("/admin/notifications/read-all")} linkFor={(n) => (n.ref_kind === "signup_request" ? "/admin/signup-requests" : null)} />;
  const top = (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">{t("admin.area")}</p>
      <p className="mt-1 font-semibold text-white">{t("admin.platform")}</p>
      {me?.demo_mode && (
        <div className="mt-2" title={t("p7.admin.demoExplain")}>
          <button type="button" onClick={() => setDemoOpen((o) => !o)} aria-expanded={demoOpen} data-testid="demo-mode-toggle"><Badge tone="amber" testId="demo-mode-badge">{t("common.demoMode")} ⓘ</Badge></button>
          <p className={`mt-2 text-[11px] leading-snug text-slate-400 ${demoOpen ? "" : "hidden lg:block"}`} data-testid="demo-mode-explain">{t("p7.admin.demoExplain")}</p>
        </div>
      )}
    </div>
  );
  return (
    <AppShell groups={groups} bottom={bottom} top={top} homeTo="/admin" mobileTitle={t("admin.platform")} actions={bell}
      tabs={[{ to: "/admin", end: true, label: t("b13.nav.home"), icon: LayoutDashboard, testId: "tab-admin-home" }, { to: "/admin/garages", label: t("admin.nav.garages"), icon: Building2, testId: "tab-admin-garages", match: ["/admin/garages", "/admin/signup-requests"] },
        { to: "/admin/support-inbox", label: t("b13.nav.support"), icon: LifeBuoy, testId: "tab-admin-support" }]}>
      <Outlet />
    </AppShell>
  );
}
